import React, { useMemo, useState } from "react";
import {
  Container,
  Row,
  Card,
  Col,
  Modal,
  Form,
  Button,
  Offcanvas,
  Badge,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import Breadcrumb from "Common/BreadCrumb";
import { Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "react-select";
import {
  useAddEnseignantMutation,
  useDeleteEnseignantMutation,
  useFetchEnseignantsQuery,
} from "features/enseignants/enseignantSlice";
import UpdateEnseignant from "./UpdateEnseignant";
import { useFetchRendezvousByEnseignantIdQuery } from "features/rendezvous/rendezvousSlice";
import {
  Matiere,
  MatieresToAdd,
  useFetchMatieresQuery,
} from "features/matieres/matiereSlice";

const Enseignants = () => {
  const { data = [] } = useFetchEnseignantsQuery();

  const { data: AllMatieres = [] } = useFetchMatieresQuery();
  const allMatieres = AllMatieres.flatMap((item) => item.matieres);

  const allMatiereNames = allMatieres.map((matiere) => matiere.nom_matiere);

  const uniqueMatiereNames = Array.from(new Set(allMatiereNames)).sort((a, b) => a.localeCompare(b, "fr"));

  const [deleteEnseignant] = useDeleteEnseignantMutation();

  const [showEnseignant, setShowEnseignant] = useState<boolean>(false);

  const notifySuccess = () => {
    Swal.fire({
      position: "center",
      icon: "success",
      title: "L'enseignant a été créé avec succès",
      showConfirmButton: false,
      timer: 2500,
    });
  };

  const notifyError = (err: any) => {
    Swal.fire({
      position: "center",
      icon: "error",
      title: `Une erreur est survenue: ${err}`,
      showConfirmButton: false,
      timer: 2500,
    });
  };

  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      confirmButton: "btn btn-success",
      cancelButton: "btn btn-danger",
    },
    buttonsStyling: false,
  });

  const AlertDelete = async (_id: any) => {
    swalWithBootstrapButtons
      .fire({
        title: "Etes-vous sûr?",
        text: "Vous ne pouvez pas revenir en arrière?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Oui, supprime-le !",
        cancelButtonText: "Non, annuler !",
        reverseButtons: true,
      })
      .then((result) => {
        if (result.isConfirmed) {
          deleteEnseignant(_id);
          swalWithBootstrapButtons.fire(
            "Supprimé !",
            "L'enseignant est supprimé.",
            "success"
          );
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          swalWithBootstrapButtons.fire(
            "Annulé",
            "L'enseignant est en sécurité :)",
            "info"
          );
        }
      });
  };

  const [modal_AddEnseignant, setmodal_AddEnseignant] =
    useState<boolean>(false);
  function tog_AddEnseignant() {
    setmodal_AddEnseignant(!modal_AddEnseignant);
  }

  const [modal_UpdateEnseignant, setmodal_UpdateEnseignant] =
    useState<boolean>(false);
  function tog_UpdateEnseignant() {
    setmodal_UpdateEnseignant(!modal_UpdateEnseignant);
  }

  const [selectedMatiere, setSelectedMatiere] = useState<string>("");

  const handleSelectMatiere = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedMatiere(value);
  };

  const [createEnseignant] = useAddEnseignantMutation();

  const initialEnseignant = {
    nom_enseignant: "",
    prenom_enseignant: "",
    phone: "",
    matiere: "",
  };

  const [enseignant, setEnseignant] = useState(initialEnseignant);

  const { nom_enseignant, prenom_enseignant, phone, matiere } = enseignant;

  const onChangeEnseignant = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEnseignant((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  };

  const onSubmitEnseignant = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      enseignant["matiere"] = selectedMatiere;
      createEnseignant(enseignant)
        .then(() => notifySuccess())
        .then(() => setEnseignant(initialEnseignant));
    } catch (error) {
      notifyError(error);
    }
  };

  // Filters
  const [filterMatiere, setFilterMatiere] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Stats
  const stats = useMemo(() => {
    const matiereCount: Record<string, number> = {};
    data.forEach((e: any) => {
      const m = e.matiere || "Non définie";
      matiereCount[m] = (matiereCount[m] || 0) + 1;
    });
    const topMatieres = Object.entries(matiereCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 2);
    return {
      total: data.length,
      topMatiere1: topMatieres[0] || null,
      topMatiere2: topMatieres[1] || null,
    };
  }, [data]);

  const getFilteredEnseignants = () => {
    let filtered = [...data];

    if (filterMatiere) {
      filtered = filtered.filter((e: any) => e.matiere === filterMatiere);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (enseignant: any) =>
          enseignant?.nom_enseignant
            ?.toLowerCase()
            .includes(term) ||
          enseignant?.prenom_enseignant
            ?.toLowerCase()
            .includes(term) ||
          enseignant?.phone
            ?.toLowerCase()
            .includes(term) ||
          enseignant?.matiere
            ?.toLowerCase()
            .includes(term)
      );
    }

    return filtered.reverse();
  };

  const getInitials = (nom: string, prenom: string) => {
    return `${(prenom || "")[0] || ""}${(nom || "")[0] || ""}`.toUpperCase();
  };

  const columns = [
    {
      name: <span className="fw-semibold fs-13">Enseignant</span>,
      selector: (row: any) => row.nom_enseignant,
      cell: (row: any) => (
        <div className="d-flex align-items-center gap-2">
          <div className="avatar-xs flex-shrink-0">
            <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-16">
              {getInitials(row.nom_enseignant, row.prenom_enseignant)}
            </div>
          </div>
          <div>
            <span className="d-block fw-medium fs-13">
              {row.prenom_enseignant} {row.nom_enseignant}
            </span>
          </div>
        </div>
      ),
      sortable: true,
      grow: 2,
    },
    {
      name: <span className="fw-semibold fs-13">Matière</span>,
      selector: (row: any) => row?.matiere!,
      cell: (row: any) => (
        <Badge bg="info-subtle" text={"info" as any} className="fs-12 px-2 py-1">
          <i className="ri-book-2-line me-1" />
          {row.matiere || "—"}
        </Badge>
      ),
      sortable: true,
    },
    {
      name: <span className="fw-semibold fs-13">N° Téléphone</span>,
      selector: (row: any) => row?.phone!,
      cell: (row: any) => (
        <span className="text-muted fs-13">
          <i className="ri-phone-line me-1 text-success" />
          {row.phone || "—"}
        </span>
      ),
      sortable: true,
    },
    {
      name: <span className="fw-semibold fs-13">Actions</span>,
      cell: (row: any) => (
        <div className="d-flex gap-1">
          <Link
            to="#"
            state={row}
            className="btn btn-soft-info btn-sm btn-icon"
            onClick={() => setShowEnseignant(true)}
            title="Voir détails"
          >
            <i className="ri-eye-line" />
          </Link>
          <Link
            to="#"
            state={row}
            className="btn btn-soft-success btn-sm btn-icon"
            onClick={() => tog_UpdateEnseignant()}
            title="Modifier"
          >
            <i className="ri-edit-2-line" />
          </Link>
          <button
            className="btn btn-soft-danger btn-sm btn-icon"
            onClick={() => AlertDelete(row._id)}
            title="Supprimer"
          >
            <i className="ri-delete-bin-2-line" />
          </button>
        </div>
      ),
    },
  ];

  const enseignantLocation = useLocation();

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f3f6f9",
        fontWeight: "600",
        fontSize: "13px",
        color: "#495057",
        borderBottom: "2px solid #e9ebec",
      },
    },
    rows: {
      style: {
        fontSize: "13px",
        "&:hover": {
          backgroundColor: "#f3f6f9",
        },
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #e9ebec",
      },
    },
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Enseignants" pageTitle="Tableau de bord" />

          {/* KPI Cards */}
          <Row className="mb-3">
            <Col sm={6} xl={3}>
              <Card className="card-animate border-0 shadow-sm cursor-pointer" onClick={() => setFilterMatiere("")}>
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                        <i className="ri-team-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">Total Enseignants</p>
                      <h4 className="mb-0">{stats.total}</h4>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="card-animate border-0 shadow-sm cursor-pointer" onClick={() => setFilterMatiere("")}>
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-success-subtle text-success rounded-circle fs-3">
                        <i className="ri-book-2-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">Matières</p>
                      <h4 className="mb-0">{uniqueMatiereNames.length}</h4>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card
                className={`card-animate border-0 shadow-sm cursor-pointer ${stats.topMatiere1 && filterMatiere === stats.topMatiere1[0] ? "border-warning border-2" : ""}`}
                onClick={() => stats.topMatiere1 && setFilterMatiere(filterMatiere === stats.topMatiere1[0] ? "" : stats.topMatiere1[0])}
              >
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-3">
                        <i className="ri-star-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">
                        {stats.topMatiere1 ? stats.topMatiere1[0] : "—"}
                      </p>
                      <h4 className="mb-0 text-warning">{stats.topMatiere1 ? stats.topMatiere1[1] : 0}</h4>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card
                className={`card-animate border-0 shadow-sm cursor-pointer ${stats.topMatiere2 && filterMatiere === stats.topMatiere2[0] ? "border-info border-2" : ""}`}
                onClick={() => stats.topMatiere2 && setFilterMatiere(filterMatiere === stats.topMatiere2[0] ? "" : stats.topMatiere2[0])}
              >
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-info-subtle text-info rounded-circle fs-3">
                        <i className="ri-star-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">
                        {stats.topMatiere2 ? stats.topMatiere2[0] : "—"}
                      </p>
                      <h4 className="mb-0 text-info">{stats.topMatiere2 ? stats.topMatiere2[1] : 0}</h4>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Data Table */}
          <Col lg={12}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="border-bottom-dashed bg-white">
                <Row className="g-3 align-items-center">
                  <Col lg={4} md={6}>
                    <div className="search-box">
                      <input
                        type="text"
                        className="form-control search"
                        placeholder="Rechercher par nom, prénom, matière..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                  </Col>
                  <Col lg={3} md={3}>
                    <select
                      className="form-select"
                      value={filterMatiere}
                      onChange={(e) => setFilterMatiere(e.target.value)}
                    >
                      <option value="">Toutes les matières</option>
                      {uniqueMatiereNames.map((m, i) => (
                        <option value={m} key={i}>{m}</option>
                      ))}
                    </select>
                  </Col>
                  <Col lg={5} md={3} className="d-flex justify-content-end gap-2">
                    {(filterMatiere || searchTerm) && (
                      <Button
                        variant="soft-secondary"
                        size="sm"
                        onClick={() => { setFilterMatiere(""); setSearchTerm(""); }}
                      >
                        <i className="ri-filter-off-line me-1" />
                        Réinitialiser
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      onClick={() => tog_AddEnseignant()}
                      className="d-flex align-items-center gap-1"
                    >
                      <i className="ri-add-line fs-16" />
                      Nouvel enseignant
                    </Button>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body className="p-0">
                <DataTable
                  columns={columns}
                  data={getFilteredEnseignants()}
                  pagination
                  paginationPerPage={15}
                  paginationRowsPerPageOptions={[10, 15, 25, 50]}
                  customStyles={customStyles}
                  noDataComponent={
                    <div className="text-center py-5">
                      <i className="ri-team-line fs-1 text-muted d-block mb-2" />
                      <p className="text-muted mb-0">Aucun enseignant trouvé</p>
                    </div>
                  }
                  paginationComponentOptions={{
                    rowsPerPageText: "Lignes par page",
                    rangeSeparatorText: "sur",
                  }}
                  highlightOnHover
                  responsive
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Add Modal */}
          <Modal
            className="fade"
            show={modal_AddEnseignant}
            onHide={() => tog_AddEnseignant()}
            centered
          >
            <Modal.Header closeButton className="bg-primary-subtle">
              <h5 className="modal-title">
                <i className="ri-add-circle-line me-2" />
                Nouvel enseignant
              </h5>
            </Modal.Header>
            <Modal.Body>
              <Form className="create-form" onSubmit={onSubmitEnseignant}>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label htmlFor="nom_enseignant">Nom</Form.Label>
                    <Form.Control
                      type="text"
                      id="nom_enseignant"
                      name="nom_enseignant"
                      onChange={onChangeEnseignant}
                      value={enseignant.nom_enseignant}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label htmlFor="prenom_enseignant">Prénom</Form.Label>
                    <Form.Control
                      type="text"
                      id="prenom_enseignant"
                      name="prenom_enseignant"
                      onChange={onChangeEnseignant}
                      value={enseignant.prenom_enseignant}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label htmlFor="phone">N° Téléphone</Form.Label>
                    <Form.Control
                      type="text"
                      id="phone"
                      name="phone"
                      onChange={onChangeEnseignant}
                      value={enseignant.phone}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label htmlFor="matiere">Matière</Form.Label>
                    <Select
                      options={uniqueMatiereNames.map((matiere) => ({
                        value: matiere,
                        label: matiere,
                      }))}
                      onChange={(opt: any) => { const e = { target: { value: opt?.value || "" } } as any; handleSelectMatiere(e); }}
                      placeholder="Rechercher une matière..."
                      isClearable
                      isSearchable
                      noOptionsMessage={() => "Aucune matière trouvée"}
                    />
                  </Col>
                </Row>
                <div className="hstack gap-2 justify-content-end mt-4">
                  <Button
                    variant="light"
                    onClick={() => {
                      tog_AddEnseignant();
                      setEnseignant(initialEnseignant);
                    }}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={() => tog_AddEnseignant()}
                    type="submit"
                    variant="primary"
                  >
                    <i className="ri-check-line me-1" />
                    Enregistrer
                  </Button>
                </div>
              </Form>
            </Modal.Body>
          </Modal>

          {/* Update Modal */}
          <Modal
            className="fade"
            show={modal_UpdateEnseignant}
            onHide={() => tog_UpdateEnseignant()}
            centered
            size="lg"
          >
            <Modal.Header closeButton className="bg-success-subtle">
              <h5 className="modal-title">
                <i className="ri-edit-2-line me-2" />
                Modifier enseignant
              </h5>
            </Modal.Header>
            <Modal.Body>
              <UpdateEnseignant
                modal_UpdateEnseignant={modal_UpdateEnseignant}
                setmodal_UpdateEnseignant={setmodal_UpdateEnseignant}
              />
            </Modal.Body>
          </Modal>
        </Container>

        {/* Detail Offcanvas */}
        <Offcanvas
          show={showEnseignant}
          onHide={() => setShowEnseignant(false)}
          placement="end"
          style={{ width: "420px" }}
        >
          <Offcanvas.Header closeButton className="border-bottom bg-light">
            <Offcanvas.Title className="d-flex align-items-center gap-2">
              <i className="ri-team-line text-primary fs-20" />
              Détails de l'enseignant
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <div className="text-center mb-4">
              <div className="avatar-md mx-auto mb-3">
                <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-24">
                  {getInitials(
                    enseignantLocation?.state?.nom_enseignant || "",
                    enseignantLocation?.state?.prenom_enseignant || ""
                  )}
                </div>
              </div>
              <h5 className="mb-1">
                {enseignantLocation?.state?.prenom_enseignant}{" "}
                {enseignantLocation?.state?.nom_enseignant}
              </h5>
            </div>

            <div className="mb-4">
              <label className="text-muted text-uppercase fs-11 fw-semibold mb-2 d-block">
                Matière
              </label>
              <Badge bg="info-subtle" text={"info" as any} className="fs-13 px-3 py-2">
                <i className="ri-book-2-line me-1" />
                {enseignantLocation?.state?.matiere || "—"}
              </Badge>
            </div>

            <Row className="mb-4 g-3">
              <Col xs={12}>
                <label className="text-muted text-uppercase fs-11 fw-semibold mb-2 d-block">
                  N° Téléphone
                </label>
                <span>
                  <i className="ri-phone-line me-1 text-success" />
                  {enseignantLocation?.state?.phone || "—"}
                </span>
              </Col>
            </Row>
          </Offcanvas.Body>
        </Offcanvas>
      </div>
    </React.Fragment>
  );
};
export default Enseignants;
